import CommonSVG from 'components/atom/SVG/CommonSVG'
import {useState} from 'react'
import {
  DRIVE_IMAGE_PREFIX,
  getFirstLetterCapitalOfWord,
  getImageUrl,
} from 'utils/ConstFunctions'
import {SVG_CROSS_RED} from 'utils/SvgConstants'
import When from 'components/when/When'
import {VideoPositionKey} from '../../types/treatmentPlan.types'
import videoUploadTypesConstants from '@constants/videoUploadTypes.constants'
import {ArrowLeft} from 'lucide-react'

export const unReviewableExtension = ['MOV0', 'mov', 'AVI', 'avi']

interface VideoFileUrls {
  SINGLE_VIDEO: IFile | null
  TOP: IFile | null
  BOTTOM: IFile | null
  RIGHT: IFile | null
  LEFT: IFile | null
  FRONT: IFile | null
}

interface CommonImageViewProps {
  videoKey: VideoPositionKey
  videoFileUrls: VideoFileUrls
  handleVideoFileRemove?: (videoKey: string) => void
  showCrossButton?: boolean
}

interface IFile {
  name: string
  url: string
  type: string
  extension: string
}

// Converts a Google Drive "view" link into an embeddable "preview" link for use in an iframe
const getDriveEmbedUrl = (url: string) => {
  if (!url) return url
  if (url.includes('/preview')) return url
  return url.replace('/view', '/preview')
}

interface DriveVideoWebviewProps {
  videoUrl: string
  fileName?: string
  onBack: () => void
}

const DriveVideoWebview: React.FC<DriveVideoWebviewProps> = ({videoUrl, fileName, onBack}) => {
  return (
    <div className='fixed top-0 left-0 w-full h-full bg-white z-[9999] flex flex-col'>
      <div className='flex items-center gap-3 px-4 py-3 border-b border-lightGray'>
        <button
          type='button'
          onClick={onBack}
          className='flex items-center justify-center w-9 h-9 rounded-full border border-mediumGray hover:bg-gray-100 transition-all'
        >
          <ArrowLeft size={18} />
        </button>
        <span className='font-medium text-sm text-textColor truncate'>
          {fileName || 'Video preview'}
        </span>
      </div>
      <div className='flex-1 bg-black'>
        <iframe
          src={getDriveEmbedUrl(videoUrl)}
          className='w-full h-full border-0'
          allow='autoplay'
          allowFullScreen
        />
      </div>
    </div>
  )
}

export const CommonVideoView: React.FC<CommonImageViewProps> = ({
  videoKey,
  videoFileUrls,
  handleVideoFileRemove,
  showCrossButton = true,
}) => {
  const [isPreviewOpen, setPreviewOpen] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [driveWebview, setDriveWebview] = useState<{isOpen: boolean; url: string; name?: string}>({
    isOpen: false,
    url: '',
    name: '',
  })

  const videoUrl = getImageUrl(videoFileUrls[videoKey]) || ''
  const fileName = videoFileUrls[videoKey]?.name

  const togglePreview = (url: string) => {
    if (!url) return

    if (url.startsWith(DRIVE_IMAGE_PREFIX)) {
      setDriveWebview({isOpen: true, url, name: fileName})
      return
    }

    // Toggle preview state
    setPreviewOpen(!isPreviewOpen)
    if (!isPreviewOpen) {
      setIsPlaying(true)
    } else {
      setIsPlaying(false)
    }
  }

  const handleCloseDriveWebview = () => {
    setDriveWebview({isOpen: false, url: '', name: ''})
  }

  const handleClosePreview = () => {
    setPreviewOpen(false)
    setIsPlaying(false)
  }

  // Prevent click from bubbling up to parent
  const handleVideoClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  if (driveWebview.isOpen) {
    return (
      <DriveVideoWebview
        videoUrl={driveWebview.url}
        fileName={driveWebview.name}
        onBack={handleCloseDriveWebview}
      />
    )
  }

  return (
    <div className='' onClick={handleVideoClick}>
      <div className='font-medium text-sm text-textColor'>
        {videoKey === videoUploadTypesConstants.SINGLE_VIDEO
          ? null
          : getFirstLetterCapitalOfWord(videoKey)}
      </div>

      <div
        className='relative h-[150px] w-[150px] border border-mediumGray rounded-xl overflow-hidden flex items-center justify-center cursor-pointer'
        onClick={() => togglePreview(videoUrl)}
      >
        {!isPreviewOpen ? (
          // Thumbnail view
          <div className='relative w-full h-full'>
            <video
              className='w-full h-full object-cover'
              src={videoUrl}
              muted
              preload='metadata'
            />
            <div className='absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hover:bg-opacity-20 transition-all'>
              <svg className='w-12 h-12 text-white' fill='currentColor' viewBox='0 0 24 24'>
                <path d='M8 5v14l11-7z' />
              </svg>
            </div>
          </div>
        ) : (
          // Playing view
          <video
            className='w-full h-full object-cover'
            src={videoUrl}
            controls
            autoPlay
            muted={false}
          />
        )}

        <When isTrue={showCrossButton}>
          <div
            className='absolute top-2 right-2 cursor-pointer w-8 h-8 bg-white rounded-full flex justify-center items-center z-10'
            onClick={(e) => {
              e.stopPropagation()
              if (handleVideoFileRemove) {
                handleVideoFileRemove(videoKey)
              }
            }}
          >
            <CommonSVG svg={SVG_CROSS_RED} width='16' height='16' />
          </div>
        </When>
      </div>

      {/* Full screen preview modal (non-Drive videos) */}
      {isPreviewOpen && (
        <div
          className='fixed top-0 left-0 w-full h-full bg-black bg-opacity-90 z-[9999] flex justify-center items-center'
          onClick={handleClosePreview}
        >
          <div
            className='relative w-[95%] h-[90%] bg-black rounded-lg'
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              className='absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75 transition-all z-10'
              onClick={handleClosePreview}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <video
              className='w-full h-full object-contain'
              src={videoUrl}
              controls
              autoPlay
              controlsList='nodownload'
            />
          </div>
        </div>
      )}
    </div>
  )
}